import type { ReactNode } from 'react';
import { ApiError } from '../api';

export function formatMoney(amountMinor: number, currency: string): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amountMinor / 100);
}

/** Payment rows get their own wording: "cancelled" there is not a subscription winding down. */
export function paymentTag(status: string, providerStatus?: string | null): ReactNode {
  const mismatch = providerStatus === 'COMPLETED_AMOUNT_MISMATCH';
  return (
    <span className={`tag ${mismatch ? 'suspended' : status === 'completed' ? 'active' : status === 'pending' ? 'past_due' : ''}`}>
      {mismatch ? 'amount mismatch' : status === 'completed' ? 'paid' : status}
    </span>
  );
}

/** Localized-ish absolute date. Admin work needs precision, not "5 min ago". */
export function formatDate(input: string | null | undefined): string {
  if (!input) return '—';
  return new Date(input).toLocaleString();
}

export function statusTag(status: string): ReactNode {
  return <span className={`tag ${status}`}>{status.replace('_', ' ')}</span>;
}

export function planTag(plan: string): ReactNode {
  return <span className={`tag ${plan === 'pro' || plan === 'business' ? 'pro' : ''}`}>{plan}</span>;
}

/**
 * Every error box on the page carries the same information: the machine
 * `code`, the human `message`, and the `requestId` — which is what a support
 * ticket needs to match up with the server log line.
 */
export function renderError(error: unknown): ReactNode {
  if (error instanceof ApiError) {
    return (
      <div className="error">
        <strong>{error.code}</strong> — {error.message}
        {error.requestId && (
          <>
            {' '}
            (request <kbd>{error.requestId}</kbd>)
          </>
        )}
      </div>
    );
  }
  return <div className="error">{String((error as { message?: string })?.message ?? error)}</div>;
}

/**
 * Prompts the operator for a reason before a mutating action. Every write on
 * the admin API takes a mandatory `reason` — the audit log is only as useful
 * as the string typed here.
 */
export async function promptForReason(action: string): Promise<string | null> {
  const value = window.prompt(`Reason for ${action}:`);
  const trimmed = value?.trim();
  if (!trimmed) return null;
  return trimmed;
}
